// Tax-invoice rates (ex GST) — the single source for every rate the Tax Invoices
// page bills at. The saved copy lives server-side (settings KV `tax_invoice_rates`,
// via tax-invoices-sb) as an append-only list of VERSIONS, each with an
// effective-from date. An invoice bills at the version in force on its period's
// start date, so an old period reprinted later keeps its old rates (Paul, 30 Oct 2026:
// the fee went up and the new rates must never revert).
//
// Nothing here is stored in the browser. CURRENT_RATES is only the fallback shown
// (with a warning) when the saved rates can't load — keep it equal to the latest
// saved version whenever a fee changes.

// Field list per invoice type — drives the Rates tab and the shape check.
export const RATE_FIELDS = {
  doe: [
    { key: 'mainRate',   label: 'Main service line / day', dp: 4 },
    { key: 'schoolRate', label: 'School / location (each)', dp: 4 },
  ],
  wave: [
    { key: 'unitPrice',  label: 'Unit price / item', dp: 4 },
    { key: 'adjPerItem', label: 'Adjustment / item (negative = reduction, 0 = none)', dp: 4 },
  ],
  night: [
    { key: 'staff',       label: 'Staff / hr' },
    { key: 'leader',      label: 'Leader / hr' },
    { key: 'staffOT',     label: 'Staff O/T' },
    { key: 'leaderOT',    label: 'Leader O/T' },
    { key: 'staffSun',    label: 'Staff / hr (Sun)' },
    { key: 'leaderSun',   label: 'Leader / hr (Sun)' },
    { key: 'staffSunOT',  label: 'Staff O/T (Sun)' },
    { key: 'leaderSunOT', label: 'Leader O/T (Sun)' },
    { key: 'parcels',     label: 'Parcels' },
  ],
  extra: [
    { key: 'staff',       label: 'Staff / hr' },
    { key: 'leader',      label: 'Leader / hr' },
    { key: 'staffOT',     label: 'Staff O/T' },
    { key: 'leaderOT',    label: 'Leader O/T' },
    { key: 'staffSun',    label: 'Staff / hr (Sun)' },
    { key: 'leaderSun',   label: 'Leader / hr (Sun)' },
    { key: 'staffSunOT',  label: 'Staff O/T (Sun)' },
    { key: 'leaderSunOT', label: 'Leader O/T (Sun)' },
  ],
};
export const RATE_TYPES = ['doe', 'wave', 'night', 'extra'];

const NIGHT = {
  staff: 52.73, leader: 58.30, staffOT: 26.37, leaderOT: 29.15,
  staffSun: 52.73, leaderSun: 58.30, staffSunOT: 26.37, leaderSunOT: 29.15,
};

// The rates in force now (fee increase, Oct 2026). DoE ex GST: 20 days × 262.9273 =
// $5,258.55; a school at 8 × 5.3208 = $42.57. Every school shares the one rate.
export const CURRENT_RATES = {
  doe:   { mainRate: 262.9273, schoolRate: 5.3208 },
  wave:  { unitPrice: 5.91, adjPerItem: -2.48 },
  night: { ...NIGHT, parcels: -0.29 },
  extra: { ...NIGHT },
};

// What the page billed before the increase — seeded once as the first history
// version so periods before the increase keep these when reprinted. Never a fallback.
export const PRE_INCREASE_RATES = {
  ...CURRENT_RATES,
  doe: { mainRate: 252.0495, schoolRate: 5.1005 },
};

const isISO = (s) => /^\d{4}-\d{2}-\d{2}$/.test((s || '').toString());

// Coerce a rates object to the full shape: every type, every field a finite number.
// Missing fields fall back to `base` (never silently to zero).
export function normaliseRates(rates, base = CURRENT_RATES) {
  const out = {};
  RATE_TYPES.forEach((t) => {
    out[t] = {};
    RATE_FIELDS[t].forEach(({ key }) => {
      const v = rates && rates[t] ? Number(rates[t][key]) : NaN;
      out[t][key] = Number.isFinite(v) ? v : base[t][key];
    });
  });
  return out;
}

// Versions ordered oldest → newest: by effective-from, then by save time, so a
// re-save on the same effective date supersedes the earlier one.
export function sortVersions(versions) {
  return (versions || []).slice().sort((a, b) =>
    (a.effectiveFrom || '').localeCompare(b.effectiveFrom || '') ||
    (a.savedAt || '').localeCompare(b.savedAt || ''));
}

// The version in force on `iso` (an invoice period's start): the newest version
// whose effective-from is on/before it. A date before every version takes the
// earliest one. Returns null when nothing is saved.
export function versionFor(versions, iso) {
  const list = sortVersions(versions);
  if (!list.length) return null;
  const d = isISO(iso) ? iso : new Date().toISOString().slice(0, 10);
  let hit = null;
  list.forEach((v) => { if ((v.effectiveFrom || '') <= d) hit = v; });
  return hit || list[0];
}

// The newest version by effective date — what the Rates tab edits and what
// new periods default to.
export function latestVersion(versions) {
  const list = sortVersions(versions);
  return list.length ? list[list.length - 1] : null;
}

// Rates for an invoice whose period starts on `iso`.
export function ratesFor(versions, iso) {
  const v = versionFor(versions, iso);
  return v ? normaliseRates(v.rates) : normaliseRates(CURRENT_RATES);
}

// True when two rate sets differ in any field (to 6 dp, so float noise isn't a change).
export function ratesDiffer(a, b) {
  return RATE_TYPES.some((t) => RATE_FIELDS[t].some(({ key }) =>
    Math.round(Number(a[t][key]) * 1e6) !== Math.round(Number(b[t][key]) * 1e6)));
}

// A CPI increase of `pct` % on the chosen invoice types. Each rate is scaled and
// rounded to its own precision (4 dp for DoE / EP, 2 dp for the hourly rates).
// Negative adjustments (EP per-item, Night parcels) scale too, so every net rate
// rises by the same percentage.
export function applyCpi(rates, pct, types = RATE_TYPES) {
  const f = 1 + Number(pct) / 100;
  const out = {};
  RATE_TYPES.forEach((t) => {
    out[t] = { ...rates[t] };
    if (!types.includes(t)) return;
    RATE_FIELDS[t].forEach(({ key, dp }) => {
      const p = 10 ** (dp || 2);
      out[t][key] = Math.round(Number(rates[t][key]) * f * p) / p;
    });
  });
  return out;
}

// The version a save would be undone back to: what was in force on the newest
// version's effective date before that version was saved. Null when there is nothing
// earlier to go back to.
export function versionBefore(versions) {
  const latest = latestVersion(versions);
  if (!latest) return null;
  const rest = (versions || []).filter((v) => v.id !== latest.id);
  return rest.length ? versionFor(rest, latest.effectiveFrom) : null;
}
